import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOrganization } from './OrganizationContext';
import agentService from '../services/agentService';
import Logger from '../utils/logger';

export const AgentContext = createContext(null);

const ACTIVE_AGENT_KEY = 'Nixia_active_agent_id';
const LEGACY_AGENT_KEY = 'nixia_active_agent_id';
const ACTIVE_AGENT_BY_ORG_KEY = 'Nixia_active_agent_by_org';
const ACTIVE_AGENT_ORG_KEY = 'Nixia_active_agent_org_id';

const readAgentMap = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_AGENT_BY_ORG_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAgentMap = (map) => {
  try {
    localStorage.setItem(ACTIVE_AGENT_BY_ORG_KEY, JSON.stringify(map || {}));
  } catch {}
};

export const AgentProvider = ({ children }) => {
  const { activeOrganization } = useOrganization();
  const [agents, setAgents] = useState([]);
  const [activeAgent, setActiveAgent] = useState(null);
  const [loading, setLoading] = useState(false);

  const persistSelection = useCallback((orgId, agentId) => {
    if (!orgId) {
      localStorage.removeItem(ACTIVE_AGENT_KEY);
      localStorage.removeItem(LEGACY_AGENT_KEY);
      localStorage.removeItem(ACTIVE_AGENT_ORG_KEY);
      return;
    }

    const map = readAgentMap();
    if (agentId) {
      map[orgId] = agentId;
      localStorage.setItem(ACTIVE_AGENT_KEY, agentId);
      localStorage.setItem(LEGACY_AGENT_KEY, agentId);
      localStorage.setItem(ACTIVE_AGENT_ORG_KEY, orgId);
    } else {
      delete map[orgId];
      localStorage.removeItem(ACTIVE_AGENT_KEY);
      localStorage.removeItem(LEGACY_AGENT_KEY);
      localStorage.removeItem(ACTIVE_AGENT_ORG_KEY);
    }
    writeAgentMap(map);
  }, []);

  const loadAgents = useCallback(async (orgId) => {
    if (!orgId) {
      setAgents([]);
      setActiveAgent(null);
      persistSelection(null, null);
      return;
    }

    setLoading(true);
    try {
      const list = await agentService.getAgents(orgId);
      const normalized = Array.isArray(list) ? list : [];
      setAgents(normalized);

      const map = readAgentMap();
      const stored = map[orgId] || null;
      const fallback = normalized.length > 0 ? normalized[0].id : null;
      const next = normalized.some(a => String(a.id) === String(stored)) ? stored : fallback;

      if (next) {
        const selected = normalized.find(a => String(a.id) === String(next)) || null;
        setActiveAgent(selected);
        persistSelection(orgId, String(next));
      } else {
        setActiveAgent(null);
        persistSelection(orgId, null);
      }
    } catch (error) {
      const expectedStatus = error?.status === 401 || error?.status === 402;
      if (!expectedStatus) {
        Logger.error('AgentContext: error loading agents', error);
      }
      setAgents([]);
      setActiveAgent(null);
      persistSelection(orgId, null);
    } finally {
      setLoading(false);
    }
  }, [persistSelection]);

  useEffect(() => {
    const orgId = activeOrganization?.id || null;
    loadAgents(orgId);
  }, [activeOrganization?.id, loadAgents]);

  useEffect(() => {
    const handleAuthChange = () => {
      setAgents([]);
      setActiveAgent(null);
      persistSelection(null, null);
      const orgId = activeOrganization?.id || null;
      if (orgId) {
        loadAgents(orgId);
      }
    };

    window.addEventListener('authStateChanged', handleAuthChange);
    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, [activeOrganization?.id, loadAgents, persistSelection]);

  const switchAgent = useCallback((agentId) => {
    const orgId = activeOrganization?.id;
    if (!orgId) return;
    const selected = agents.find(a => String(a.id) === String(agentId)) || null;
    setActiveAgent(selected);
    persistSelection(orgId, selected ? String(selected.id) : null);
  }, [activeOrganization?.id, agents, persistSelection]);

  const value = useMemo(() => ({
    agents,
    activeAgent,
    loading,
    switchAgent,
    refreshAgents: () => loadAgents(activeOrganization?.id || null),
  }), [agents, activeAgent, loading, switchAgent, loadAgents, activeOrganization?.id]);

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
